import isArray from 'lodash/isArray';
import { isEqual, uniqWith } from 'lodash';
import { normalize } from 'normalizr';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import fromUnixTime from 'date-fns/fromUnixTime';
import { format, addYears, parseISO } from 'date-fns';
import isNaN from 'lodash/isNaN';
import { getUser } from './auth';
import { ContractFilterStatus } from '../constants';

/**
 * Use a custom merge function for immutable, because by default they concat arrays
 * (i.e. locations) and we want them to just overwrite them. i.e. if a user deletes
 * a zip, it wouldn't reflect in the state because the new smaller array would
 * just combine with the old one.
 */
// eslint-disable-next-line consistent-return
export default function mergeOverwriteArrays(a, b) {
  if (isArray(a)) return b;
}

// eslint-disable-next-line consistent-return
export function mergeArrays(a, b) {
  if (isArray(a)) {
    return uniqWith(a.concat(b), isEqual);
  }
}
/**
 * Take a plain object and remove null or undefined properties.
 *
 * @param {object} obj - Object with values.
 */
export const removeEmptyProperties = obj => {
  Object.keys(obj).forEach(k => {
    if (obj[k] === null || obj[k] === undefined || obj[k] === '') {
      // eslint-disable-next-line no-param-reassign
      delete obj[k];
    }
  });
  return obj;
};

/**
 * Take a plain object and convert it into a url encoded query string.
 *
 * @param {object} parameters - Query parameters.
 */
export const buildQueryString = (parameters = {}) => {
  const groomedParameters = removeEmptyProperties(parameters);
  return Object.entries(groomedParameters)
    .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
    .join('&');
};

/**
 * convert camel case string to normal sentence
 *
 * @param {string} text - The string in camel case to format.
 * @returns {string} - Formatted sentence.
 */
export const convertCamelCase = text => {
  const result = text.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

/**
 * Helper for handling successful API responses.
 *
 * @param {normalizr.schema} schema - Normalizr data schema object to use.
 * @param {object} action - Redux action.
 * @param {object} state - Redux app state.
 * @param {Response} res - Fetch API response object.
 */
export const handleResponse = async (schema, action, state, res) => {
  const json = await res.json();
  // prevent re-renders simply because a modified date changes
  delete json.modified_date;
  return normalize(json, schema);
};

/**
 * Helper for handling successful bundle search API responses.
 *
 * @param {object} action - Redux action.
 * @param {object} state - Redux app state.
 * @param {Response} res - Fetch API response object.
 */
export const handleBundleSearchResponse = async (action, state, res) => {
  const json = await res.json();
  return json;
};

/**
 * Helper for handling unsuccessful API responses.
 *
 * @param {object} action - Redux action.
 * @param {object} state - Redux app state.
 * @param {Response} res - Fetch API response object.
 */
export const handleError = async (action, state, res) => {
  let body;
  try {
    body = await res.json();
  } catch (err) {
    body = null;
  }

  return {
    message:
      body?.message ||
      (res.status === 401 && 'Your session has expired. You need to login again') ||
      (res.status === 404 && 'Item not found') ||
      'An error occured.',
    secondaryMessage: body?.secondaryMessage,
    status: res.status,
    body,
  };
};

/**
 * Debounce for handling delay in API invoke.
 *
 * @param {object} fn - React function.
 * @param {object} delay - delay in ms.
 */
export const debounce = (fn, delay) => {
  let timer = null;
  const debouncer = (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
  return debouncer;
};

export const capitalizeWordsInString = str => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.substring(1))
    .join('');
};
/**
 * Helper to convert pixels to rem units to help make sure we stay consistent w/ wireframes that list pixels only.
 * Assumes a root font style of 16px (browser set).
 *
 * @param {int} px - Pixels to convert to rem
 */
export const pxToRem = px => `${px * 0.0625}rem`;

/**
 * Format a date using the day, month and year.
 *
 * Removing the special designator 'Z' at the end wouldnt let it change to local timezone.
 *
 * @param {string} date - The dateString to format.
 * @param {string} [pattern='MM/dd/yyyy'] - Formatting pattern. See date-fns for details.
 * @param {boolean} [localize=false] - Set to false you don't want the date to get converted to local time.
 * @returns {string} - A formatted datestring.
 */
export const formatDateString = (date, pattern = 'MM/dd/yyyy', localize = false) => {
  if (!date || typeof date !== 'string') return date;
  let utcDate = date;
  if (!localize) {
    utcDate = utcDate.replace(/Z$/, '');
  }
  const dateObj = new Date(utcDate);
  return date ? format(parseISO(dateObj.toISOString()), pattern) : '';
};

export const getUTCHours = date => {
  const dateObj = new Date(date);
  return date ? dateObj.getUTCHours() : '';
};

export const getUTCMinutes = date => {
  const dateObj = new Date(date);
  return dateObj.getUTCMinutes(date);
};

export const getMonthFromMonthDayYear = date => {
  // gets month Name from mm/dd/yyyy
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const d = new Date(date);
  return monthNames[d.getMonth()];
};

/**
 * Normalizes comma separated date to 'YYYY-MM-DD'
 * Example date arguement: "May 6, 2020"
 * @param {string} date
 */

const getMonthFromString = mon => {
  return new Date(Date.parse(`${mon} 1, 2020`)).getMonth() + 1;
};
export const normalizeDate = date => {
  const dateSplit = date.split(',', 2);
  const month = getMonthFromString(dateSplit[0].split(' ', 2)[0]);
  const day = dateSplit[0].split(' ', 2)[1];
  const year = dateSplit[1].trim();
  return format(new Date(`${month}/${day}/${year}`), 'MM/dd/yyyy');
};

export const addTimeToDate = (date, time) => {
  const timeSplit = time.split(' ', 2);
  const units = timeSplit[0];
  const endDate = addYears(new Date(date), units);
  return format(endDate, 'MM/dd/yyyy');
};

/**
 * Format a currency using the Internationalization NumberFormat API.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat options}
 *
 * @param {string|Int} amount - The amount to format.
 * @param {string|String} currency - The currency used to format.
 * @param {object} [options] - `Intl.DateTimeFormat` options.
 * @returns {Intl.DateTimeFormat} - Formatted currency.
 */
export const formatCurrency = (amount, currency, options) => {
  const internalOptions = {
    style: 'currency',
    currency,
    minimumIntegerDigits: 1,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    ...options,
  };
  return isNaN(amount)
    ? ''
    : new Intl.NumberFormat(navigator.language, internalOptions).format(amount);
};
export const formatNumberWithCommas = x => {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const removePunctuation = str => {
  // eslint-disable-next-line no-useless-escape
  const punctuationLess = str.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '');
  const finalWord = punctuationLess.replace(/\s{2,}/g, ' ');
  return finalWord;
};

/**
 * Format an array into a sentence style format.
 *
 * @param {array} options - The date to format.
 * @returns {string} - Formatted sentence.
 */
export const sentenceFromArray = options => {
  if (options && options.length) {
    if (options.length > 1) {
      return `${options.slice(0, options.length - 1).join(', ')}, and ${options.slice(-1)}`;
    }
    if (options.length === 1) {
      return `${options[0]}`;
    }
  }
  return null;
};

/**
 * Formatted complete sentence based on options.
 *
 * @param {object} options -  options: { type, geographies, channels, currencies, products } - The options to build out a custom sentence about options.
 * @returns {string} - Formatted complete sentence based on options.
 */
export const filterSentence = options => {
  const { type, geographies, currencies, channels, products } = options;
  const capitalAndPluralizedType = `${type.charAt(0).toUpperCase() + type.slice(1)}s`;
  const geographySentence = sentenceFromArray(geographies);
  const productSentence = sentenceFromArray(products);
  const currencySentence = sentenceFromArray(currencies);
  const channelSentence = sentenceFromArray(channels);

  const productHandle = productSentence ? `<strong>${productSentence}</strong>` : '';
  const geographyHandle = geographySentence ? `in <strong>${geographySentence}</strong>` : '';
  const currencyHandle = currencySentence ? `with <strong>${currencySentence}</strong>` : '';
  const channelHandle = channelSentence ? `via <strong>${channelSentence}</strong>` : '';

  return {
    __html: `<p><strong>${capitalAndPluralizedType}</strong> ${productHandle} transactions ${geographyHandle} ${currencyHandle} ${channelHandle}</p>`,
  };
};

/**
 * Take array of key values and return the array of text values instead
 *
 * @param {array} keyArray -  The array of key values you want to match to text values
 * @param {array} initialArray -  The initial array you want to look up what key value matches the text value
 * @returns {string} - Returns a prettier array of the text options instead of the uglier key value
 */
export const mapKeyToText = (keyArray, initialArray) => {
  if (keyArray.length && initialArray.length) {
    return keyArray.map(key => {
      const targetObject = initialArray.find(target => target.key === key);
      // if target object was found, return the text property, otherwise
      // just return the given key
      if (targetObject && targetObject.text) {
        return targetObject.text;
      }
      return key;
    });
  }

  return null;
};

/**
 * Take array of objects with key & text props. Find the object with the provided
 * key and return the text value for that item.
 *
 * @param {array} items -  The array of objects you're searching in for a specific `key`.
 * @param {string} key -  The `key` of the object you want to find.
 * @returns {string|boolean} - Returns the text property of the object with the provided `key`. Or false.
 */
export const getTextFromKey = (items, key) => {
  if (items.length) {
    const item = items.find(i => i.key === key);
    if (item && item.text) {
      return item.text;
    }
    return false;
  }

  return null;
};

/**
 * Take array of objects with tokenId & name props. Find the object with the provided
 * tokenId and return the array of name value for that item.
 *
 * @param {array} items -  The array of objects you're searching for a specific `tokenId`.
 * @param {string} tokenIds -  The array of tokenId you want to find.
 * @returns {string|boolean} - Returns the array of name matching the provided `tokenId`.
 */
export const getProductNameFromTokenId = (items, tokenIds) => {
  if (items.length && tokenIds.length) {
    let products = items.filter(item => tokenIds.includes(item.tokenId));
    products = products.length ? products.map(product => product.name) : [];
    return products;
  }
  return null;
};

/**
 * Get "status" of a contract.
 *
 * @param {string|Date} effective - Effective date of the contract.
 * @param {string|Date} termination - Termination date of the contract.
 * @param {string} approvalStatus - Approval status of the contract.
 * @returns {string} - Returns a status slug based on contract terms.
 */
export const contractStatus = (effective, termination, approvalStatus) => {
  const effectiveDate = new Date(effective).getTime();
  const terminationDate = new Date(termination).getTime();
  const currentDate = new Date().getTime();
  const inSetupStatus = ['Draft', 'PendingInitialApproval', 'PendingApproval', 'Paused'];

  switch (true) {
    case inSetupStatus.includes(approvalStatus):
      return ContractFilterStatus.InSetup;
    case currentDate < effectiveDate:
      return ContractFilterStatus.SetupComplete;
    case currentDate > effectiveDate && currentDate < terminationDate:
      return ContractFilterStatus.Active;
    case currentDate > terminationDate:
      return ContractFilterStatus.Expired;
    default:
      return 'default';
  }
};

/**
 * Get "status" of a bundle.
 *
 * @param {string|Date} startDate - start date of the bundle.
 * @param {string|Date} endDate - expiry date of the bundle.
 * @param {boolean} locked - locked status of the bundle.
 * @param {boolean} isPending - if going to revise the bundle.
 * @returns {string} - Returns a status slug based on bundle status.
 */
export const bundleStatus = (endDate, locked, isPending) => {
  const expiryDate = new Date(endDate).getTime();
  const lock = isPending || locked;
  const currentDate = new Date().getTime();
  switch (true) {
    case !lock && currentDate < expiryDate:
      return 'active';
    case currentDate > expiryDate:
      return 'expired';
    case lock:
      return 'pending';
    default:
      return 'default';
  }
};

/**
 * Determine if fieldLength is too long.
 *
 * @param {*} fieldName
 * @param {*} limit
 */
export const isFieldLengthTooLong = (fieldName, limit) => {
  return fieldName.length > limit;
};

/**
 * Returns the remaining time left for logged in user, in milliseconds.
 *
 * @param {*} user
 */
export const getRemainingLoginTime = user =>
  differenceInMilliseconds(
    fromUnixTime(localStorage.getItem(`adal.expiration.key${user.profile.aud}`)),
    new Date(),
  );

/**
 * Check if any line item has a Draft status, for the given adjustment
 *
 * @param {object} adjustmentObj - Adjustment object
 */
export const isAnyLineItemInDraft = adjustmentObj => {
  if (!adjustmentObj || !adjustmentObj.lineItems || adjustmentObj.lineItems.length === 0) {
    return true;
  }
  return adjustmentObj.lineItems.some(lineItem => lineItem.status.toLowerCase() === 'draft');
};

/**
 * Check if an adjustment is in draft, or has line items in draft state
 * @param {object} adjustmentObj - Adjustment object
 * @returns {boolean} true if adjustment is in draft or has any line items in draft
 */
export const isDraftAdjustment = adjustment => {
  if (!adjustment) {
    return false;
  }

  return adjustment.status.toLowerCase() === 'draft' || isAnyLineItemInDraft(adjustment);
};

/**
 * Check if an adjustment is rejected
 * @param {object} adjustmentObj - Adjustment object
 * @returns {boolean} true if adjustment is in draft AND has a rejectReason
 */
export const isRejectedAdjustment = adjustment => {
  return isDraftAdjustment(adjustment) && !!adjustment.rejectReason;
};

/**
 * Check if the currently logged in user is the reviewer of the adjustment
 * @param {object} adjustmentObj - Adjustment object
 * @returns {boolean} true if the current user is the reviewer, otherwise false
 */
export const isReviewerOfAdjustment = adjustment => {
  if (!adjustment) {
    return false;
  }

  const user = getUser();
  return user?.profile.oid === adjustment.reviewer?.reviewerId;
};

/**
 * Check if the currently logged in user is the creator of the adjustment
 * @param {object} adjustmentObj - Adjustment object
 * @returns {boolean} true if the current user is the creator, otherwise false
 */
export const isCreatorOfAdjustment = adjustment => {
  if (!adjustment) {
    return false;
  }

  const user = getUser();
  return user?.profile.oid === adjustment.reviewer?.reviewerId;
};

/**
 * Check if the currently logged in user is the reviewer of the dimensionList
 * @param {object} dimensionList - Dimension List object
 * @returns {boolean} true if the current user is the reviewer, otherwise false
 */
export const isReviewerOfDimensionList = dimensionList => {
  if (!dimensionList) {
    return false;
  }

  const user = getUser();
  return user?.profile.oid === dimensionList.reviewerId;
};

/**
 * Converts seconds into human readable minutes
 *
 * @param {*} seconds
 */
// eslint-disable-next-line
export const secondsToMinutes = seconds =>
  (seconds - (seconds %= 60)) / 60 + (9 < seconds ? ':' : ':0') + Math.floor(seconds); // eslint-disable-line

/**
 * Check if the browser is mobile app webview.
 */
export const isMobileApp = () => window.navigator.userAgent.includes('RoyaltiesMobileApp');

/**
 * Bypasses market screen
 */
export const bypassMarketPage = () => window.navigator.userAgent.includes('Bypass-Market');

/**
 * No operation
 */
export const noop = () => {};

/**
 * Returns an object after modifying the column names
 * @param {object} results - User object
 * @returns {object} parsedUser returns the modidied object
 */
export const parseUserData = results => {
  const userData = results.data?.slice(0, -1);
  const parsedUser = userData.map(user => ({
    mail: user.CompanyEmail,
    role: user.UserRole,
    contractId: user.ContractID,
  }));
  return parsedUser;
};
